import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import debounce from 'lodash/debounce';

// @zillow / @zg-rentals
import { CometTheme } from '@zillow/zrs-comet';
import { Footer } from '@zillow/zrs-comet';
import { HeaderNavigation } from '@zg-rentals/zrm-utils-js';

// Actions
import AppActions from 'app/shared/flux/actions/AppActions';

// Misc / Utils
import * as S from './AppStyles';
import { getDevice } from 'app/shared/utils/browserUtils';
import constants from 'app/shared/constants/ConstantsBundle';
import getRoutes from 'app/shared/utils/routes';
import { webVitals_initialize } from 'app/shared/utils/webVitalsUtils';

// Types
import type { State } from 'app/shared/types/State';

const applyRegLoginStyles = () => {
    // @ts-ignore
    import('@zillow/reg-login/src/less/reg-login.less');
};

const metaDescriptionContent =
    'Use Zillow Rental Manager to manage applications, screen tenants, collect rent and list ' +
    'your rental property on Zillow, Trulia and HotPads.';
const METAS = [{ name: 'description', content: metaDescriptionContent }];
const TITLE = 'How much should I charge for rent? – Rent Comp Estimator | Zillow Rental Manager';
const PROXY_PATH = '/' + constants.PROXY_PATH.slice(0, constants.PROXY_PATH.length - 1); // remove trailing forward slash

const App = () => {
    // Redux
    const dispatch = useDispatch();
    const csrfToken = useSelector((state: State) => state.app.csrfToken);
    const pathname = useSelector((state: State) => state.router.location.pathname);
    const queryString = useSelector((state: State) => state.router.location.search);
    const user = useSelector((state: State) => state.user);

    const { features } = user;
    const displayAnalytics = features.canAccessMainAnalytics;

    const setDeviceAndWidth = debounce(() => {
        const device = getDevice();
        dispatch(AppActions.updateApp({ device }));
    }, 100);

    useEffect(() => {
        // Google Web Vitals tracking
        webVitals_initialize();
        setDeviceAndWidth();
        window.addEventListener('resize', setDeviceAndWidth);
    }, []);

    useEffect(applyRegLoginStyles, []);

    return (
        <ThemeProvider theme={CometTheme}>
            <S.ResetStyles />

            <S.AppContainer>
                <Helmet meta={METAS} title={TITLE} />

                <HeaderNavigation
                    altLogo={true}
                    brand={constants.ZILLOW_BRAND}
                    csrf_token={csrfToken}
                    displayAnalytics={displayAnalytics}
                    pathname={pathname + queryString}
                    proxyPath={PROXY_PATH}
                    useRelativePaths={false}
                    userModel={user}
                />

                {getRoutes()}

                <S.FooterContainer>
                    <Footer />
                </S.FooterContainer>
            </S.AppContainer>
            <S.GlobalStyle />
        </ThemeProvider>
    );
};

export default App;
